import { Card, CardContent, Typography, Grid, CardActions, Button, CardProps } from '@mui/material'
import { Box } from '@mui/system'
import Carousel from 'react-material-ui-carousel'
import { CardImage } from '../components/CardImage'
import { PriceTag } from '../components/PriceTag'
import { IHotelAvailability, IRoomInfo } from '../types/hotelTypes'
import { formatCurrency } from '../utils/numberUtil'
import { Loader } from '../components/Loader'
import { PriceOptions } from '../types/bookingFormTypes'
import { useNavigate } from 'react-router'
import { useContext } from 'react'
import { HotelContext } from '../contexts/HotelContext'
import qs from 'qs'
import { DateTime } from 'luxon'
import { formatHotelDateTime } from '../utils/dateUtils'

export interface IRoomSearchResultProps extends CardProps {
  item: IRoomInfo
  error?: any
  isLoading?: boolean
  availability?: IHotelAvailability
  searchParams?: {
    priceOption?: PriceOptions
    from?: DateTime
    to?: DateTime
    nights?: number
    hours?: number
  }
  readonly?: boolean
}

export const RoomSearchResult = ({
  item,
  availability,
  isLoading,
  error,
  searchParams,
  readonly,
  ...props
}: IRoomSearchResultProps) => {
  const isValid = !isLoading && !error && availability?.TotalPrice > 0
  const navigate = useNavigate()
  const { hotel } = useContext(HotelContext)

  return (
    <Card {...props} sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Carousel
        indicators={false}
        sx={{
          zIndex: 1,
        }}
        navButtonsAlwaysVisible
        navButtonsProps={{
          style: {
            background: 'none',
          },
        }}>
        {item.roomImages?.map((roomImage, i) => (
          <CardImage key={i} image={roomImage} />
        ))}
      </Carousel>
      <CardContent sx={{ px: 1, py: 0, flex: 1 }}>
        <Typography sx={{ mt: 1 }} variant="h6" fontSize="1.3rem">
          {item.roomTypeName}
        </Typography>
        <Grid container flex={1}>
          <Grid item xs={12} sm={isValid ? 6 : 12}>
            <Typography
              fontSize="1rem"
              color={(theme) => theme.palette.grey[600]}
              sx={{
                whiteSpace: 'pre-line',
                lineHeight: 'normal',
              }}>
              {item.roomDescription}
            </Typography>
          </Grid>
          {isValid && (
            <Grid
              item
              xs={12}
              sm={6}
              alignItems="flex-end"
              justifyContent="space-between"
              display="flex"
              flexDirection="column">
              <Box textAlign="right">
                <PriceTag height="1rem" color="primary" label={formatCurrency(availability.TotalPrice)} />
                {Boolean(searchParams?.hours && searchParams?.priceOption === PriceOptions.Hour) && (
                  <Typography color={(theme) => theme.palette.grey[700]} fontSize="1rem" fontWeight={500}>
                    {searchParams?.hours} giờ
                  </Typography>
                )}
                {Boolean(searchParams?.nights && searchParams?.priceOption !== PriceOptions.Hour) && (
                  <Typography color={(theme) => theme.palette.grey[700]} fontSize="1rem" fontWeight={500}>
                    {searchParams?.nights} đêm
                  </Typography>
                )}
              </Box>
            </Grid>
          )}
        </Grid>
      </CardContent>
      {!readonly && (
        <CardActions sx={{ justifyContent: 'flex-end' }}>
          {isValid && Boolean(availability?.AvailableRooms) && (
            <Button
              onClick={() =>
                navigate({
                  pathname: `/hotel/${hotel.hotelId}/room/${item.roomTypeId}`,
                  search: qs.stringify({
                    ...searchParams,
                    from: searchParams?.from ? formatHotelDateTime(searchParams.from) : undefined,
                    to: searchParams?.to ? formatHotelDateTime(searchParams.to) : undefined,
                  }),
                })
              }
              size="small">
              Chọn phòng
            </Button>
          )}
          {isValid && !availability?.AvailableRooms && (
            <Button disabled variant="outlined" color="error" size="small">
              Hết Phòng
            </Button>
          )}
          {isLoading && <Loader />}
        </CardActions>
      )}
    </Card>
  )
}
